import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Constants, Helpers } from "utils";
import { Box, Grid, Typography } from "@mui/material";
import { Card, Custom, Modal, Progress } from "components";
import { IAppointment, IQarePlan, IService } from "interfaces";

export const Journey = () => {
  const { t, theme, locale, service: providedService } = useApp();

  const [service, setService] = useState<IService>({} as IService);
  const [currentLength, setCurrentLength] = useState(0);
  const [healthGoalIds, setHealthGoalIds] = useState<number[]>([]);
  const [filterOpened, setFilterOpened] = useState(false);
  const [serviceOpened, setServiceOpened] = useState(false);
  const [appointmentOpened, setAppointmentOpened] = useState(false);
  const [appointments, setAppointments] = useState<IAppointment[] | null>(null);
  const [qarePlan, setQarePlan] = useState<IQarePlan | null>(null);
  const [qarePlanOpened, setQarePlanOpened] = useState(false);

  const boxes = useRef<Map<number, Element>>(new Map());

  useEffect(() => {
    appointmentsEffect();
  }, [healthGoalIds]);

  const appointmentsEffect = async () => {
    const response = await providedService.appointment.slice(
      healthGoalIds,
      currentLength,
      Constants.MAX_INTEGER
    );

    // TODO: lazy programming
    response.appointments.forEach((appointment: IAppointment) => {
      if (appointment.qarePlan) {
        appointment.qarePlan.clinic = appointment.clinic;
        appointment.qarePlan.reason = appointment.reason;
      }
    });

    setAppointments(response.appointments);
  };

  const countAppointments = () => {
    return (
      appointments?.filter((x: IAppointment) =>
        Helpers.compareTo(new Date(x.scheduledTime), new Date())
      ).length ?? 0
    );
  };

  const getAppointmentMessage = (item: IAppointment) => {
    return item.clinic
      ? Helpers.interpolate(t.message.appointment.consult_clinic, {
          name: item.clinic.username,
        })
      : Helpers.interpolate(t.message.appointment.consult_provider, {
          name: item.provider.username,
        });
  };

  const handleAppointmentOpen = (service: IService) => {
    setService(service);
    setAppointmentOpened(true);
    setServiceOpened(false);
  };

  const handleAppointmentClose = () => {
    handleQarePlanClose();
    setAppointments(null);
    setAppointmentOpened(false);
    // TODO: probably incorrect
    appointmentsEffect();
  };

  const handleServiceOpen = () => {
    setAppointmentOpened(false);
    setServiceOpened(true);
  };

  const handleServiceClose = () => setServiceOpened(false);

  const handleFilterOpen = () => setFilterOpened(true);

  const handleFilterClose = (healthGoalIds: number[]) => {
    setFilterOpened(false);
    setHealthGoalIds(healthGoalIds);
  };

  const handleQarePlanOpen = (qarePlan: IQarePlan) => {
    setQarePlan(qarePlan);
    setQarePlanOpened(true);
  };

  const handleQarePlanClose = () => {
    setQarePlan(null);
    setQarePlanOpened(false);
  };

  const handleAppointmentGoBack = () =>
    qarePlan ? setAppointmentOpened(false) : handleServiceOpen();

  const handleQarePlanAppointmentOpen = () => {
    handleAppointmentOpen(qarePlan?.service as IService);
  };

  const getDateTime = (scheduledTime: Date) => {
    const date = scheduledTime
      .toLocaleDateString(locale, Constants.DATE_FORMAT)
      .split(",")[0];
    const time = scheduledTime.toLocaleTimeString(
      locale,
      Constants.TIME_FORMAT
    );

    const style = { marginLeft: "0", display: "block" };
    return (
      <>
        <div style={style}>{date}</div>
        <div style={style}>{time}</div>
      </>
    );
  };

  let currentYear: number = -1;

  return (
    <>
      <div className="qh-header">
        <Grid container>
          <Grid container item xs={12} sm={6}>
            {/* <div className="qh-title">
              <Typography component="span" sx={{ fontSize: theme.font.md }}>
                {t.title.patient.journey}
              </Typography>
            </div> */}
          </Grid>
          <Grid container item xs={12} sm={6} justifyContent="flex-end">
            <div className="qh-control">
              <Custom.Button variant="contained" onClick={handleServiceOpen}>
                {t.action.make_appointment}
              </Custom.Button>
              <Modal.MakeAppointment
                service={service}
                opened={appointmentOpened}
                onBack={handleAppointmentGoBack}
                onClose={handleAppointmentClose}
                {...(qarePlan && { qarePlan, clinic: qarePlan.clinic })}
              />
              <Modal.Service
                opened={serviceOpened}
                onClose={handleServiceClose}
                onClick={(service: IService) => handleAppointmentOpen(service)}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <div className="qh-content">
        <Custom.Fab variant={"filter"} onClick={handleFilterOpen} />
        <Grid container sx={{ justifyContent: "center" }}>
          <Grid item xs={12} sm={6} lg={4}>
            {appointments ? (
              appointments.length > 0 ? (
                <Custom.Timeline>
                  {appointments.map((appointment: IAppointment) => {
                    const scheduledTime = new Date(appointment.scheduledTime);
                    const year = scheduledTime.getFullYear();
                    appointment.healthGoals = appointment.healthGoals ?? [];
                    const element: React.ReactNode = (
                      <Box
                        key={appointment.appointmentId}
                        ref={(box: Element) =>
                          boxes.current.set(appointment.appointmentId, box)
                        }
                        sx={{ scrollMarginTop: "4rem" }}
                      >
                        {currentYear !== year && (
                          <Custom.TimelineItem dot={year.toString()} />
                        )}
                        <Custom.TimelineItem
                        // header={getDateTime(scheduledTime)}
                        >
                          <Card.Appointment
                            sx={{ marginBottom: theme.margin.lg }}
                            appointment={appointment}
                            handleQarePlanOpen={handleQarePlanOpen}
                            getDateTime={getDateTime}
                          />
                        </Custom.TimelineItem>
                      </Box>
                    );
                    currentYear = year;
                    return element;
                  })}
                </Custom.Timeline>
              ) : (
                <Progress.NoRecord />
              )
            ) : (
              <Progress.PageProcess />
            )}
          </Grid>
        </Grid>
      </div>
      <div className="qh-timeline-nav">{t.title.patient.journey}</div>
      <div className="qh-timeline-nav qh-timeline-nav-content">
        {appointments?.length && (
          <Custom.Timeline>
            {appointments.map((appointment: IAppointment) => (
              <Box
                key={appointment.appointmentId}
                onClick={() => {
                  boxes.current
                    .get(appointment.appointmentId)
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                sx={{ "&:hover": { cursor: "pointer" } }}
              >
                <Custom.TimelineItem>
                  {getDateTime(new Date(appointment.scheduledTime))}
                </Custom.TimelineItem>
              </Box>
            ))}
          </Custom.Timeline>
        )}
      </div>
      <Modal.QarePlanPatient
        qarePlan={qarePlan}
        opened={qarePlanOpened}
        onClose={handleQarePlanClose}
        handleQarePlanAppointmentOpen={handleQarePlanAppointmentOpen}
      />
      <Modal.FilterJourney
        opened={filterOpened}
        onClose={handleFilterClose}
        value={healthGoalIds}
      />
    </>
  );
};
